import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authAPI, clientsAPI, licenseAPI } from '../services/api'; // Changed from '../services/authService' to match api.js export
import { toast } from 'react-hot-toast';

const BROADCAST_CHANNEL_NAME = 'pom_auth_channel';
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [activePortal, setActivePortal] = useState(() => localStorage.getItem('activePortal') || 'client');
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);
  const activityHandlerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const sanitizeClientId = (value) => {
    const s = String(value ?? '').trim()
    if (!/^\d+$/.test(s)) return ''
    const n = Number(s)
    return n > 0 ? s : ''
  }

  const ensureSelectedClientId = useCallback(async () => {
    try {
      if (activePortal !== 'client') return
      const existing = sanitizeClientId(localStorage.getItem('selectedClientId'))
      if (existing) return
      const res = await clientsAPI.getAll()
      const list = res?.data?.data?.clients || res?.data?.data || []
      const active = Array.isArray(list) ? list.filter((c) => c.status === 'Active') : []
      if (active.length > 0 && active[0]?.id) {
        localStorage.setItem('selectedClientId', String(active[0].id))
      }
    } catch (_) { void 0 }
  }, [activePortal])

  const switchPortal = (portal) => {
    if (portal !== 'client' && portal !== 'vendor') return;
    setActivePortal(portal);
    localStorage.setItem('activePortal', portal);
    toast.success(`Switched to ${portal === 'client' ? 'Client' : 'Vendor'} Portal`);
  };

  const logout = useCallback(async (isRemote = false) => {
    try {
      if (!isRemote) {
        // Call backend to invalidate session
        await authAPI.logout(); 
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('activePortal');
      sessionStorage.removeItem('token');
      setUser(null);
      setActivePortal('client');
      if (!isRemote) {
        toast.success('Logged out successfully');
        navigate('/login');
      } else {
        // Remote logout (e.g. from another tab or idle) - also redirect
        navigate('/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    // Safety timeout to ensure app doesn't get stuck in loading state
    const safetyTimeout = setTimeout(() => {
      setLoading((currentLoading) => {
        if (currentLoading) {
          console.warn('Auth initialization timed out safely, forcing app load');
          return false;
        }
        return currentLoading;
      });
    }, 7000);

    const initAuth = async () => {
      console.log('initAuth started');
      try {
        const localToken = localStorage.getItem('token')
        const sessionToken = sessionStorage.getItem('token')
        if (localToken && !sessionToken) {
          try {
            sessionStorage.setItem('token', localToken)
          } catch (_) { void 0 }
        }
        const token = sessionStorage.getItem('token') || localStorage.getItem('token')
        
        // Always check license status first or in parallel
        try {
          console.log('Checking license status...');
          // Add a timeout to prevent hanging indefinitely
          const licensePromise = licenseAPI.getStatus();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('License check timed out')), 5000)
          );
          
          const licenseRes = await Promise.race([licensePromise, timeoutPromise]);
          console.log('License check response:', licenseRes);
          
          // Handle both success: true wrapper AND direct status response
          if (licenseRes.data) {
             if (licenseRes.data.success && licenseRes.data.data) {
                setLicenseStatus(licenseRes.data.data);
             } else if (licenseRes.data.status === 'NoLicense') {
                setLicenseStatus({ valid: false, message: 'No License Found', code: 'NO_LICENSE' });
             } else if (licenseRes.data.status === 'Active') {
                // If backend returns unwrapped object
                setLicenseStatus(licenseRes.data);
             }
          }
        } catch (err) {
          console.error('Failed to fetch license status:', err);
          // Default to invalid if check fails, but don't block the UI
          setLicenseStatus({ valid: false, error: 'Connection Error' });
        }

        if (token) {
          try {
            console.log('Checking auth token...');
            // Add timeout for auth check as well
            const authPromise = authAPI.getMe();
            const authTimeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Auth check timed out')), 5000)
            );
            
            const response = await Promise.race([authPromise, authTimeoutPromise]);
            console.log('Auth check response:', response);
            setUser(response.data);
            try {
              const scopedLicense = await licenseAPI.getStatus(response.data?.tenant_id);
              if (scopedLicense?.data) {
                if (scopedLicense.data.success && scopedLicense.data.data) {
                  setLicenseStatus(scopedLicense.data.data)
                } else if (scopedLicense.data.status === 'NoLicense') {
                  setLicenseStatus({ valid: false, message: 'No License Found', code: 'NO_LICENSE' })
                } else if (scopedLicense.data.status === 'Active') {
                  setLicenseStatus(scopedLicense.data)
                }
              }
            } catch (_) { void 0 }
            await ensureSelectedClientId()
          } catch (error) {
            console.error('Auth initialization failed:', error);
            // Only clear token if it's a 401/403, not a timeout/network error
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
              localStorage.removeItem('token');
              sessionStorage.removeItem('token');
              setUser(null);
            }
            // For other errors, we assume token might be valid but network failed
            // However, to be safe and avoid stale state, we might want to clear user
            setUser(null);
          }
        }
      } catch (globalError) {
        console.error('Critical error in initAuth:', globalError);
      } finally {
        console.log('initAuth finished, setting loading to false');
        setLoading(false);
        clearTimeout(safetyTimeout);
      }
    };

    initAuth();
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  // Sync activePortal with user permissions
  useEffect(() => {
    if (user) {
      // Check if user is Admin - Admins can access both portals regardless of portal_access setting
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      const isAdmin = roles.includes('Admin') || roles.includes('SuperAdmin');
      
      if (isAdmin) return;

      if (user.portal_access === 'Vendor Portal' && activePortal !== 'vendor') {
        setActivePortal('vendor');
      } else if (user.portal_access === 'Client Portal' && activePortal !== 'client') {
        setActivePortal('client');
      }
    }
  }, [user, activePortal]);

  const login = async (email, password, rememberMe, force = false) => {
    const response = await authAPI.login({ email, password, force });
    const { token, ...userData } = response.data;
    
    setUser(userData);
    
    // Set active portal based on access
    if (userData.portal_access === 'Vendor Portal') {
        setActivePortal('vendor');
        localStorage.setItem('activePortal', 'vendor');
    } else if (userData.portal_access === 'Client Portal') {
        setActivePortal('client');
        localStorage.setItem('activePortal', 'client');
    } else {
        // Both - respect last used or default to client
        const lastUsed = localStorage.getItem('activePortal');
        if (!lastUsed || (lastUsed !== 'client' && lastUsed !== 'vendor')) {
             setActivePortal('client');
             localStorage.setItem('activePortal', 'client');
        }
    }
    
    if (rememberMe) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token')
    }
    sessionStorage.setItem('token', token)

    try {
      const scopedLicense = await licenseAPI.getStatus(userData.tenant_id)
      if (scopedLicense?.data) {
        if (scopedLicense.data.success && scopedLicense.data.data) {
          setLicenseStatus(scopedLicense.data.data)
        } else if (scopedLicense.data.status === 'NoLicense') {
          setLicenseStatus({ valid: false, message: 'No License Found', code: 'NO_LICENSE' })
        } else if (scopedLicense.data.status === 'Active') {
          setLicenseStatus(scopedLicense.data)
        }
      }
    } catch (_) { void 0 }

    await ensureSelectedClientId()
    
    return response;
  };

  const refreshMe = useCallback(async () => {
    const response = await authAPI.getMe();
    setUser(response.data);
    return response.data;
  }, []);

  useEffect(() => {
    // Initialize BroadcastChannel safely
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        channelRef.current.onmessage = (event) => {
          if (event.data.type === 'ACTIVITY') {
            activityHandlerRef.current?.(true)
          }
        };
      }
    } catch (e) {
      console.warn('BroadcastChannel not supported or failed to initialize', e);
    }

    // Listen for unauthorized API responses
    const handleUnauthorized = () => {
      logout(true);
    };
    window.addEventListener('unauthorized', handleUnauthorized);

    const handleSessionTimeout = async () => {
      try {
        await authAPI.ping()
        await refreshMe()
      } catch (_) { void 0 }
    }
    window.addEventListener('session-timeout', handleSessionTimeout)

    return () => {
      channelRef.current?.close();
      window.removeEventListener('unauthorized', handleUnauthorized);
      window.removeEventListener('session-timeout', handleSessionTimeout)
    };
  }, [logout, refreshMe]);

  // Session Activity Monitor
  useEffect(() => {
    if (!user) return () => {}

    const IDLE_TIMEOUT_MS = 5 * 60 * 1000
    const PING_THROTTLE_MS = 60 * 1000

    let activityTimer = null
    let lastPingAt = 0
    let lastBroadcastAt = 0
    const passiveOptions = { passive: true }

    const safePing = async () => {
      try {
        await authAPI.ping()
      } catch (_) { void 0 }
    }

    const scheduleIdleLogout = () => {
      if (activityTimer) clearTimeout(activityTimer)
      activityTimer = setTimeout(() => {
        toast.error('Session timed out due to inactivity.')
        logout()
      }, IDLE_TIMEOUT_MS)
    }

    const onActivity = (fromRemote = false) => {
      scheduleIdleLogout()
      const now = Date.now()
      if (now - lastPingAt >= PING_THROTTLE_MS) {
        lastPingAt = now
        safePing()
      }
      if (!fromRemote && now - lastBroadcastAt >= 2000) {
        lastBroadcastAt = now
        channelRef.current?.postMessage({ type: 'ACTIVITY', at: now })
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onActivity(false)
    }

    activityHandlerRef.current = onActivity

    onActivity(false)

    window.addEventListener('mousemove', onActivity)
    window.addEventListener('keydown', onActivity)
    window.addEventListener('click', onActivity)
    window.addEventListener('scroll', onActivity, passiveOptions)
    window.addEventListener('pointerdown', onActivity)
    window.addEventListener('pointermove', onActivity)
    window.addEventListener('wheel', onActivity, passiveOptions)
    window.addEventListener('touchstart', onActivity, passiveOptions)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      if (activityTimer) clearTimeout(activityTimer)
      if (activityHandlerRef.current === onActivity) activityHandlerRef.current = null
      window.removeEventListener('mousemove', onActivity)
      window.removeEventListener('keydown', onActivity)
      window.removeEventListener('click', onActivity)
      window.removeEventListener('scroll', onActivity, passiveOptions)
      window.removeEventListener('pointerdown', onActivity)
      window.removeEventListener('pointermove', onActivity)
      window.removeEventListener('wheel', onActivity, passiveOptions)
      window.removeEventListener('touchstart', onActivity, passiveOptions)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [user, logout]);

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    // Admin has all permissions (checking if role array contains Admin or if role string is Admin)
    const roles = Array.isArray(user.role) ? user.role : [user.role];
    if (roles.includes('Admin') || roles.includes('SuperAdmin')) return true;
    
    return user.permissions?.includes(permission);
  };

  // Check if user has any of the required permissions
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    const roles = Array.isArray(user.role) ? user.role : [user.role];
    if (roles.includes('Admin') || roles.includes('SuperAdmin')) return true;
    
    return permissions.some(p => user.permissions?.includes(p));
  };

  // Mock enabled modules for Config-driven UI
  const enabledModules = user?.tenant?.modules || [
    'dashboard', 'budget', 'procurement', 'approvals', 'sourcing', 'finance', 'inventory', 'grc', 'integration', 'platform', 'settings'
  ];

  const value = {
    user,
    activePortal,
    switchPortal,
    licenseStatus,
    isViewOnly: licenseStatus ? !licenseStatus.valid : false,
    loading,
    login,
    logout,
    refreshMe,
    hasPermission,
    hasAnyPermission,
    isAuthenticated: !!user,
    enabledModules
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 font-medium animate-pulse">Initializing System...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
